import { useCallback, useMemo, useRef, useState } from "react";
import type { Dispatch, MutableRefObject, SetStateAction } from "react";
import type {
  BuyerResearchConversationEntry,
  BuyerResearchConversationRoute,
} from "@/api/buyerResearch";

/**
 * The conversation's own cross-step state, owned ABOVE the component that
 * reads it.
 *
 * <p>🔴 <b>THIS IS THE SAME DEFECT `useResearchCount` EXISTS FOR, ONE FIELD
 * OVER, AND IT IS THE ONE THE OWNER NAMED AS THE GOVERNING RISK OF THE STEPPED
 * FORM.</b> The five-step form renders ONE step at a time — it has to, because
 * `useFocusTrap`'s selector has no visibility filter, so a hidden-but-mounted
 * step's inputs stay inside the Tab cycle. So the conversation unmounts the
 * moment a specialist steps off it. Left as component state, the transcript, a
 * pending route offer and an unsent sentence would all die on a Back click and
 * come back blank — and `settled`, which is derived from the transcript, would
 * read FALSE on the remount while the parent's own copy of it still read true.
 * Two surfaces, one fact, opposite answers.</p>
 *
 * <p>The durable copy of a transcript lives server-side (the per-user resume
 * blob, written by each turn and read by the brief endpoint on the NEXT open),
 * so nothing is permanently lost either way. What is lost is the CURRENT
 * visit's, which no round trip in this session will bring back: the seeding
 * effect is once-per-open behind a ref, and re-seeding from the resume blob
 * would re-announce "picked up where you left off" every time somebody
 * returned to the step.</p>
 *
 * <p><b>`turnError` is deliberately NOT here.</b> It describes the last
 * attempt, and an attempt is over by the time a specialist has walked to
 * another step and back; carrying it forward would re-raise an alert about
 * something they have already moved past. Everything else in this object is
 * either the record of the conversation or the specialist's own unsent words.</p>
 */
export interface ResearchConversationState {
  /** The transcript, oldest first. */
  entries: BuyerResearchConversationEntry[];
  setEntries: Dispatch<SetStateAction<BuyerResearchConversationEntry[]>>;
  /** Appends one entry. Stable, so it can sit in an effect's dependencies. */
  append: (entry: BuyerResearchConversationEntry) => void;
  /** The composer's unsent text. */
  instruction: string;
  setInstruction: Dispatch<SetStateAction<string>>;
  /** A route the server has offered and the specialist has not answered. */
  route: BuyerResearchConversationRoute | null;
  setRoute: Dispatch<SetStateAction<BuyerResearchConversationRoute | null>>;
  /** Route clauses the specialist accepted. */
  kept: string[];
  setKept: Dispatch<SetStateAction<string[]>>;
  /**
   * Whether the resume blob has been seeded into `entries`. A ref rather than
   * state, because the seeding effect reads it in the same tick it sets it —
   * the once-per-open idiom the offer wizard uses for the same hazard.
   */
  seeded: MutableRefObject<boolean>;
}

export function useResearchConversationState(): ResearchConversationState {
  const [entries, setEntries] = useState<BuyerResearchConversationEntry[]>([]);
  const [instruction, setInstruction] = useState("");
  const [route, setRoute] = useState<BuyerResearchConversationRoute | null>(null);
  const [kept, setKept] = useState<string[]>([]);
  const seeded = useRef(false);

  const append = useCallback(
    (entry: BuyerResearchConversationEntry) => setEntries((prev) => [...prev, entry]),
    [],
  );

  // Memoised on the values it carries, so the object's identity changes only
  // when one of them does.
  //
  // 🔴 NO CONSUMER DEPENDS ON THE WHOLE OBJECT TODAY, and an earlier version of
  // this comment claimed the seeding effect did. It does not: it destructures,
  // and its dependency array names `setEntries` (stable from `useState`) and
  // `seeded` (a ref), both of which are stable whether or not this memo is
  // here. So the memo buys nothing at present. It is kept because putting the
  // whole object in a dependency array is the shape a consumer reaches for,
  // and without it that consumer's effect would re-run on every parent render
  // — a defect that would be introduced at the CALL SITE and diagnosed here.
  return useMemo(
    () => ({
      entries,
      setEntries,
      append,
      instruction,
      setInstruction,
      route,
      setRoute,
      kept,
      setKept,
      seeded,
    }),
    [entries, append, instruction, route, kept],
  );
}
